import React, { useEffect, useState } from 'react';
import {
  Card,
  CardHeader,
  Box,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Radio, RadioGroup,
  TablePagination,
  FormLabel,
  Button,
  IconButton,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import Swal from 'sweetalert2';
import axios from 'axios';

const apiUrl = 'https://api.academy.eurekagroup.id/course-quizzes';
const courseApiUrl = 'https://api.academy.eurekagroup.id/courses';
const courseDetailApiUrl = (courseId) => `https://api.academy.eurekagroup.id/course-details/sort/${courseId}`;

const QuizPage = () => {
  const [quizzes, setQuizzes] = useState([]);
  const [courses, setCourses] = useState([]);
  const [courseDetails, setCourseDetails] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedQuiz, setSelectedQuiz] = useState(null);
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState('');
  const [idCourse, setIdCourse] = useState('');
  const [idCourseDetail, setIdCourseDetail] = useState('');
  const [sort, setSort] = useState(1);
  const [selectedCourse, setSelectedCourse] = useState('');

  useEffect(() => {
    fetchQuizzes();
    fetchCourses();
  }, []);

  useEffect(() => {
    if (idCourse) {
      fetchCourseDetails(idCourse);
    }
  }, [idCourse]);

  const fetchQuizzes = async () => {
    try {
      const response = await axios.get(apiUrl);
      setQuizzes(response.data);
    } catch (error) {
      console.error('Error fetching quizzes:', error);
    }
  };

  const fetchCourses = async () => {
    try {
      const response = await axios.get(courseApiUrl);
      setCourses(response.data);
    } catch (error) {
      console.error('Error fetching courses:', error);
    }
  };

  const fetchCourseDetails = async (courseId) => {
    try {
      const response = await axios.get(courseDetailApiUrl(courseId));
      setCourseDetails(response.data);
    } catch (error) {
      console.error('Error fetching course details:', error);
    }
  };

  const handleSaveQuiz = async () => {
    if (!question || !idCourse || !idCourseDetail) {
      Swal.fire({
        title: 'Error',
        text: 'Please fill in all required fields!',
        icon: 'error',
        confirmButtonText: 'OK',
      });
      return;
    }
  
    const quizData = { id: selectedQuiz?._id, id_course: idCourse, id_course_detail: idCourseDetail, sort, question, answer };
    const token = localStorage.getItem('token'); // Ambil token dari localStorage
  
    try {
      if (selectedQuiz) {
        // Edit existing quiz
        await axios.put(`${apiUrl}/edit`, quizData, {
          headers: {
            Authorization: `Bearer ${token}`, // Menambahkan Bearer Token
          },
        });
        setQuizzes(
          quizzes.map((quiz) => (quiz._id === selectedQuiz._id ? { ...quiz, ...quizData } : quiz))
        );
        Swal.fire('Success', 'Quiz updated successfully', 'success');
      } else {
        // Add new quiz
        const response = await axios.post(`${apiUrl}/add`, quizData, {
          headers: {
            Authorization: `Bearer ${token}`, // Menambahkan Bearer Token
          },
        });
        setQuizzes([...quizzes, response.data]);
        Swal.fire('Success', 'Quiz added successfully', 'success');
      }
      closeDialog();
    } catch (error) {
      console.error('Error saving quiz:', error.response?.data || error.message);
      Swal.fire('Error', 'Failed to save quiz', 'error');
    }
  };  

  const handleDeleteQuiz = async (id) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: 'You will not be able to recover this quiz!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'No, cancel!',
    });
  
    if (result.isConfirmed) {
      const token = localStorage.getItem('token'); // Ambil token dari localStorage
  
      try {
        await axios.delete(`${apiUrl}/delete/${id}`, {
          headers: {
            Authorization: `Bearer ${token}`, // Menambahkan Bearer Token
          },
        });
        setQuizzes(quizzes.filter((quiz) => quiz._id !== id));
        Swal.fire('Deleted!', 'Quiz has been deleted.', 'success');
      } catch (error) {
        console.error('Error deleting quiz:', error);
        Swal.fire('Error', 'Failed to delete quiz', 'error');
      }
    }
  };  

  const openDialog = (quiz) => {
    if (quiz) {
      setSelectedQuiz(quiz);
      setQuestion(quiz.question);
      setAnswer(quiz.answer || '');
      setIdCourse(quiz.id_course_detail?.id_course._id || '');
      setIdCourseDetail(quiz.id_course_detail?._id || '');
      setSort(quiz.sort);
    } else {
      setSelectedQuiz(null);
      setQuestion('');
      setAnswer('');
      setIdCourse('');
      setIdCourseDetail('');
      setSort(1);
    }
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
  };

  const filteredQuizzes = selectedCourse
  ? quizzes.filter((quiz) => quiz.id_course._id === selectedCourse)
  : quizzes;

  return (
    <>
      <Card>
        <CardHeader
          title="Quiz Management"
          action={
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => openDialog(null)}
            >
              Add Quiz
            </Button>
          }
        />
<CardContent>
  <Box sx={{ marginBottom: 2 }}>
    <FormControl fullWidth margin="dense">
      <InputLabel>Filter by Course</InputLabel>
      <Select
        value={selectedCourse}
        onChange={(e) => setSelectedCourse(e.target.value)}
      >
        <MenuItem value="">All Courses</MenuItem>
        {courses.map((course) => (
          <MenuItem key={course._id} value={course._id}>
            {course.title_course}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  </Box>

  <TableContainer>
    <Table>
      <TableHead>
        <TableRow>
          <TableCell>No</TableCell>
          <TableCell>Course</TableCell>
          <TableCell>Question</TableCell>
          <TableCell>Answer</TableCell>
          <TableCell>Sort</TableCell>
          <TableCell>Actions</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {filteredQuizzes
          .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
          .map((quiz, index) => (
            <TableRow key={quiz._id}>
              <TableCell>{page * rowsPerPage + index + 1}</TableCell>
              <TableCell>{quiz.id_course?.title_course}</TableCell>
              <TableCell>{quiz.question}</TableCell>
              <TableCell>{quiz.answer}</TableCell>
              <TableCell>{quiz.sort}</TableCell>
              <TableCell>
                <IconButton onClick={() => openDialog(quiz)}>
                  <EditIcon />
                </IconButton>
                <IconButton onClick={() => handleDeleteQuiz(quiz._id)}>
                  <DeleteIcon />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
      </TableBody>
    </Table>
  </TableContainer>
  <TablePagination
    rowsPerPageOptions={[5, 10, 25]}
    component="div"
    count={filteredQuizzes.length}
    rowsPerPage={rowsPerPage}
    page={page}
    onPageChange={(e, newPage) => setPage(newPage)}
    onRowsPerPageChange={(e) => {
      setRowsPerPage(parseInt(e.target.value, 10));
      setPage(0);
    }}
  />
</CardContent>

      </Card>

      {/* Dialog */}
      <Dialog
  open={dialogOpen}
  onClose={() => {} /* Kosongkan fungsi ini agar tidak menutup saat klik luar */}
  disableBackdropClick // Ini mencegah dialog hilang saat klik di luar
>
        <DialogTitle>{selectedQuiz ? 'Edit Quiz' : 'Add Quiz'}</DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="dense" error={!idCourse}>
            <InputLabel>Course</InputLabel>
            <Select
              value={idCourse}
              onChange={(e) => setIdCourse(e.target.value)}
            >
              {courses.map((course) => (
                <MenuItem key={course._id} value={course._id}>
                  {course.title_course}
                </MenuItem>
              ))}
            </Select>
            {!idCourse && <FormHelperText>Course is required</FormHelperText>}
          </FormControl>

          <FormControl fullWidth margin="dense" error={!idCourseDetail}>
            <InputLabel>Pembahasan</InputLabel>
            <Select
              value={idCourseDetail}
              onChange={(e) => setIdCourseDetail(e.target.value)}
            >
              {courseDetails.map((course) => (
                <MenuItem key={course._id} value={course._id}>
                  {course.title_session}
                </MenuItem>
              ))}
            </Select>
            {!idCourseDetail && <FormHelperText>Pembahasan is required</FormHelperText>}
          </FormControl>

          <TextField
            fullWidth
            label="Question"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            margin="dense"
            variant="outlined"
            error={!question}
            helperText={!question ? 'Question is required' : ''}
          />
         <Box sx={{ padding: 2, border: '1px solid #ccc', borderRadius: 2, maxWidth: 400, margin: 'auto' }}>
      <FormControl component="fieldset" fullWidth>
        <FormLabel component="legend" sx={{ marginBottom: 1, fontWeight: 'bold', color: 'primary.main' }}>
          Select Your Answer
        </FormLabel>
        <RadioGroup
          value={answer}
          onChange={(e) => setAnswer(e.target.value)}
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            gap: 2,
            marginTop: 1,
          }}
        >
          <FormControlLabel value="A" control={<Radio />} label="A" />
          <FormControlLabel value="B" control={<Radio />} label="B" />
          <FormControlLabel value="C" control={<Radio />} label="C" />
          <FormControlLabel value="D" control={<Radio />} label="D" />
        </RadioGroup>
      </FormControl>
      <Typography variant="body2" color="text.secondary" sx={{ marginTop: 2, textAlign: 'center' }}>
        Current Answer: <strong>{answer || 'None'}</strong>
      </Typography>
    </Box>
          <TextField
            fullWidth
            label="Sort"
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            margin="dense"
            variant="outlined"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button color="primary" onClick={handleSaveQuiz}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default QuizPage;